import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';

const useRetainScrollPosition = location => {
  // We're trying to retain the scroll position a user get to before the page finishes loading.
  const [cachedPathname, setCachedPathname] = useState('');

  // Get isBackNavigation from Redux store
  const isBackNavigation = useSelector(state => state.router?.isBackNavigation || false);

  // Check if this is a page reload or direct URL access
  const isPageReloadOrDirectAccess = useRef(true);

  // Use localStorage to persist scroll positions across app restarts
  const getScrollPositions = () => {
    try {
      const savedPositions = localStorage.getItem('scrollPositions');
      return savedPositions ? JSON.parse(savedPositions) : {};
    } catch (err) {
      console.warn('Failed to get scrollPositions from localStorage', err);
      return {};
    }
  };

  const setScrollPosition = (pathname, position) => {
    try {
      const positions = getScrollPositions();
      positions[pathname] = position;
      localStorage.setItem('scrollPositions', JSON.stringify(positions));
    } catch (err) {
      console.warn('Failed to save scrollPosition to localStorage', err);
    }
  };

  const getScrollPosition = pathname => {
    try {
      const positions = getScrollPositions();
      return positions[pathname] || 0;
    } catch (err) {
      console.warn('Failed to get scrollPosition for pathname', pathname, err);
      return 0;
    }
  };

  const currentScrollY = useRef(0);

  // Add a scroll event listener to track the current scroll position
  useEffect(() => {
    const handleScroll = () => {
      currentScrollY.current = window.scrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Effect to check if this is a page reload or direct URL access
  useEffect(() => {
    // If this is a page reload or direct URL access, clear the stored positions
    if (isPageReloadOrDirectAccess.current) {
      try {
        localStorage.removeItem('scrollPositions');
        console.info('Reset scroll positions due to page reload or direct URL access');
      } catch (err) {
        console.warn('Failed to reset scrollPositions in localStorage', err);
      }
      isPageReloadOrDirectAccess.current = false;
    }
  }, []);

  useEffect(
    () => {
      const { scrollY, scrollX } = window;

      // If the current page isn't the last page we remember and
      // there's actually some scrolling activities, let's not interfere
      if (cachedPathname === location.pathname && (scrollY !== 0 || scrollX !== 0)) {
        setCachedPathname(location.pathname);
        return;
      }

      // For mobile app: if navigating away from any page, store the scroll position
      if (cachedPathname && cachedPathname !== location.pathname) {
        // Use the tracked scroll position instead of window.scrollY
        const actualScrollY = currentScrollY.current > 0 ? currentScrollY.current : scrollY;
        setScrollPosition(cachedPathname, actualScrollY);
        console.info(`Saved scroll position for ${cachedPathname}:`, actualScrollY);
      }

      // For mobile app: if returning to a previously visited page using back button, restore the scroll position
      const savedPosition = getScrollPosition(location.pathname);
      if (isBackNavigation && cachedPathname !== location.pathname && savedPosition > 0) {
        // Use a longer timeout to ensure the DOM has fully updated
        const scrollToPosition = () => {
          window.scrollTo(0, savedPosition);
          console.info(
            `Restored scroll position for ${location.pathname} (back navigation):`,
            savedPosition,
          );
        };

        // Try multiple times with increasing delays to ensure it works
        setTimeout(scrollToPosition, 0);
        setTimeout(scrollToPosition, 100);
        setTimeout(scrollToPosition, 500);

        setCachedPathname(location.pathname);
        return;
      }

      // But if we do remember the page the pathname changed then let's reset the scroll.
      window.scrollTo(0, 0);

      setCachedPathname(location.pathname);
    },
    [location.pathname],
  );
};

export default useRetainScrollPosition;
